import * as React from "react";
import { cn } from "@/lib/utils";

export type CardStatus = "success" | "warning" | "danger" | "info" | "neutral";

const RIBBON_BG: Record<CardStatus, string> = {
  success: "bg-rd-success-500",
  warning: "bg-rd-warning-500",
  danger: "bg-rd-danger-500",
  info: "bg-rd-info-500",
  neutral: "bg-border-default",
};

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  status?: CardStatus;
}

const CardRoot = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, status, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "relative overflow-hidden rounded-[14px] border bg-card text-card-foreground shadow-sm",
        className
      )}
      data-status={status ?? undefined}
      {...props}
    >
      {status ? (
        <span
          aria-hidden="true"
          className={cn("absolute inset-x-0 top-0 h-[3px]", RIBBON_BG[status])}
        />
      ) : null}
      {children}
    </div>
  )
);
CardRoot.displayName = "Card";

export const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col gap-1.5 p-6", className)} {...props} />
  )
);
CardHeader.displayName = "CardHeader";

export const CardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  // eslint-disable-next-line jsx-a11y/heading-has-content -- card title recebe children via spread {...props}; lint nao detecta
  <h3
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props}
  />
));
CardTitle.displayName = "CardTitle";

export const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
CardDescription.displayName = "CardDescription";

export const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
  )
);
CardContent.displayName = "CardContent";

export const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center p-6 pt-0", className)} {...props} />
  )
);
CardFooter.displayName = "CardFooter";

export const Card = Object.assign(CardRoot, {
  Head: CardHeader,
  Body: CardContent,
  Foot: CardFooter,
  Title: CardTitle,
  Description: CardDescription,
});
